import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data"],
  _excluded2 = ["pagedQuantityPack"];
import { GET_PAGED_QUANTITY_PACK } from 'pricing-pages-api/purchase-options/queries';
import { useCallback, useMemo, useRef } from 'react';
import { useAppSelector } from '../reducers';
import { quantityPackForProductApiNameSelector } from '../selectors/ProductsSelectors';
import { useGlobalLoading } from './loadingHooks';
import { usePurchaseableProductsForTerm, useQueryVariables } from './useBulkPurchaseOptions';
import useDebouncedQuery from './useDebouncedQuery';
const PAGE_LENGTH = 10;

/**
 * additionalQuantity is the total quantity minus the included amount.
 * e.g. 4000 Starter Marketing Contacts is displayed, 1000 are included, so additionalQuantity = 3000
 */
export const usePagedQuantityPack = ({
  productApiName,
  quantityPackApiName,
  additionalQuantity,
  queryParamTerm,
  pageLength = PAGE_LENGTH,
  onCompleted,
  onError
}) => {
  const defaultQueryVariables = useQueryVariables();
  const isAppReady = useGlobalLoading();
  const quantityPackDetails = useAppSelector(state => isAppReady && quantityPackApiName && productApiName ? quantityPackForProductApiNameSelector(state, {
    productApiName,
    quantityPackApiName
  }) : null);

  // Get the payment term and commitment term from the QP parent product
  const {
    purchaseOptionsForTerm,
    loading: purchaseOptionsLoading
  } = usePurchaseableProductsForTerm({
    apiName: productApiName,
    queryParamTerm
  });
  const quantityName = useMemo(() => {
    switch (quantityPackApiName) {
      case 'additional-legacy-contacts':
      case 'additional-marketing-contacts':
        return 'CONTACTS';
      case 'additional-sales-seats':
        return 'SALES_SEAT';
      case 'additional-commerce-seats':
        return 'COMMERCE_SEAT';
      case 'additional-service-seats':
        return 'SERVICE_SEAT';
      // existing behavior was to return SERVICE_SEAT as fallback
      default:
        return 'SERVICE_SEAT';
    }
  }, [quantityPackApiName]);
  const loading = purchaseOptionsLoading || !isAppReady;
  const skip = loading || !quantityPackDetails || !purchaseOptionsForTerm;
  const memoizedOptions = useMemo(() => {
    return {
      skip,
      onCompleted,
      onError,
      variables: Object.assign({}, defaultQueryVariables, {
        // query is skipped if quantityPackDetails are null, but TS complains without this fallback
        skuUuid: (quantityPackDetails === null || quantityPackDetails === void 0 ? void 0 : quantityPackDetails.skuUuid) || '',
        commitmentTerm: (purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 ? void 0 : purchaseOptionsForTerm.commitmentTerm) || 12,
        paymentTerm: (purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 ? void 0 : purchaseOptionsForTerm.paymentTerm) || 12,
        additionalQuantity,
        quantityName,
        pageLength
      })
    };
  }, [additionalQuantity, defaultQueryVariables, onCompleted, onError, pageLength, purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 ? void 0 : purchaseOptionsForTerm.commitmentTerm, purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 ? void 0 : purchaseOptionsForTerm.paymentTerm, quantityName, quantityPackDetails === null || quantityPackDetails === void 0 ? void 0 : quantityPackDetails.skuUuid, skip]);
  const _useDebouncedQuery = useDebouncedQuery(300, GET_PAGED_QUANTITY_PACK, memoizedOptions),
    {
      data
    } = _useDebouncedQuery,
    queryResults = _objectWithoutPropertiesLoose(_useDebouncedQuery, _excluded);
  return Object.assign({}, queryResults, {
    pagedQuantityPack: data === null || data === void 0 ? void 0 : data.pagedQuantityPack
  });
};

/**
 *
 * @param productApiName ApiName type
 * @param quantityPackApiName QuantityPackApiName type
 * @param additionalQuantity  e.g. 4000 Starter Marketing Contacts is displayed, 1000 are included, so additionalQuantity = 3000
 * @param queryParamTerm MONTHLY or ANNUAL
 * @returns
 */
export const usePriceForQuantityPackQuantity = ({
  productApiName,
  quantityPackApiName,
  additionalQuantity,
  queryParamTerm,
  onCompleted,
  onError
}) => {
  var _pagedQuantityPack$qu;
  // using a ref so the callback doesn't have stale data after the debounce
  const additionalQuantityRef = useRef(additionalQuantity);
  additionalQuantityRef.current = additionalQuantity;
  const onCompletedCallback = useCallback(data => {
    var _data$pagedQuantityPa;
    const quantityPackPrice = data === null || data === void 0 || (_data$pagedQuantityPa = data.pagedQuantityPack) === null || _data$pagedQuantityPa === void 0 || (_data$pagedQuantityPa = _data$pagedQuantityPa.quantities) === null || _data$pagedQuantityPa === void 0 ? void 0 : _data$pagedQuantityPa[additionalQuantityRef.current];
    if (onCompleted) {
      onCompleted({
        quantityPackPrice
      });
    }
  }, [additionalQuantityRef, onCompleted]);
  const _usePagedQuantityPack = usePagedQuantityPack({
      productApiName,
      quantityPackApiName,
      additionalQuantity,
      queryParamTerm,
      pageLength: PAGE_LENGTH,
      onCompleted: onCompletedCallback,
      onError
    }),
    {
      pagedQuantityPack
    } = _usePagedQuantityPack,
    queryResult = _objectWithoutPropertiesLoose(_usePagedQuantityPack, _excluded2);

  /**
   * From the paged result, find the requested quantity
   */
  const quantityPackPrice = pagedQuantityPack === null || pagedQuantityPack === void 0 || (_pagedQuantityPack$qu = pagedQuantityPack.quantities) === null || _pagedQuantityPack$qu === void 0 ? void 0 : _pagedQuantityPack$qu[additionalQuantity];
  return Object.assign({}, queryResult, {
    quantityPackPrice
  });
};